/**
 * React-free "store" for screens. Wrap in useSyncExternalStore:
 *
 *   const q = useMemo(() => services.live(['appointments'], () => services.app.getTodayView(id)), [id]);
 *   const state = useSyncExternalStore(q.subscribe, q.getSnapshot);
 *   // state: { status: 'loading' } | { status: 'ready', data } | { status: 'error', error }
 *
 * getSnapshot returns the same object until the data actually reloads, as useSyncExternalStore requires.
 */
import type { EntityName } from '../domain/types';
import type { StorageAdapter, Unsubscribe } from './types';

export type LiveState<T> = { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };
export interface LiveQuery<T> {
  subscribe(cb: () => void): Unsubscribe;
  getSnapshot(): LiveState<T>;
  /** Re-run the loader now. */
  refresh(): Promise<void>;
}

export function createLiveQuery<T>(
  storage: StorageAdapter,
  entities: readonly EntityName[],
  load: () => Promise<T>,
  /** Extra triggers, e.g. clock.subscribe, for views that depend on "now". */
  also: Array<(cb: () => void) => Unsubscribe> = [],
): LiveQuery<T> {
  let state: LiveState<T> = { status: 'loading' };
  const listeners = new Set<() => void>();
  let detach: Unsubscribe[] = [];
  let generation = 0;
  let queued = false;

  async function refresh(): Promise<void> {
    const mine = ++generation;
    try {
      const data = await load();
      if (mine === generation) state = { status: 'ready', data };
    } catch (error) {
      if (mine === generation) state = { status: 'error', error };
    }
    if (mine === generation) for (const cb of [...listeners]) cb();
  }
  const schedule = (): void => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; void refresh(); });
  };

  return {
    subscribe(cb) {
      listeners.add(cb);
      if (listeners.size === 1) {
        detach = [...entities.map((e) => storage.subscribe(e, schedule)), ...also.map((s) => s(schedule))];
        void refresh();
      }
      return () => {
        listeners.delete(cb);
        if (listeners.size === 0) { for (const u of detach) u(); detach = []; generation++; }
      };
    },
    getSnapshot: () => state,
    refresh,
  };
}
